// Small, framework-agnostic reader over a vendored OAS document — used by
// the operation-page components so a full ReadMe-style page can be
// described in a few lines instead of hand-coding every field. Deliberately
// dumb: it only reads what the Movider specs actually use (form-urlencoded
// *and* JSON request bodies, query/path params, $ref'd response schemas
// with `example` values), not the full OpenAPI parameter/schema surface.

export type PlaygroundField = {
  name: string;
  in: "query" | "path" | "formData";
  type: string;
  required: boolean;
  description?: string;
};

export type PlaygroundResponse = {
  status: string;
  description: string;
  contentTypes: string[];
  example: unknown;
};

export type PlaygroundOperation = {
  method: string;
  path: string;
  server: string;
  summary: string;
  description?: string;
  fields: PlaygroundField[];
  responses: PlaygroundResponse[];
  acceptContentTypes: string[];
  /**
   * Set for operations whose body is `application/json` rather than
   * form-urlencoded — 10 of the 16 operations that take a body, including
   * Create campaign, Send voice call and the WhatsApp send. Those bodies are
   * nested (arrays of objects), so they get one JSON editor seeded with
   * `example` instead of a row of flat inputs.
   */
  jsonBody?: { contentType: string; example: string };
};

function resolveSchema(spec: any, schema: any): any {
  if (schema?.$ref) {
    const key = schema.$ref.replace("#/components/schemas/", "");
    return spec.components?.schemas?.[key] ?? {};
  }
  return schema ?? {};
}

// Builds an example value for a schema using the OAS `example` annotations
// already present on the Movider specs, falling back to a type-shaped
// placeholder when a leaf has none.
function exampleFromSchema(spec: any, schemaIn: any, depth = 0): unknown {
  const schema = resolveSchema(spec, schemaIn);
  if (schema.example !== undefined) return schema.example;
  if (depth > 6) return null;

  if (schema.type === "array") {
    return [exampleFromSchema(spec, schema.items, depth + 1)];
  }
  if (schema.type === "object" || schema.properties) {
    const out: Record<string, unknown> = {};
    for (const [key, prop] of Object.entries<any>(schema.properties ?? {})) {
      out[key] = exampleFromSchema(spec, prop, depth + 1);
    }
    return out;
  }
  if (schema.type === "integer" || schema.type === "number") return 0;
  if (schema.type === "boolean") return false;
  return "";
}

export function readOperation(
  spec: any,
  operationId: string,
): PlaygroundOperation {
  const server = spec.servers?.[0]?.url ?? "";

  for (const [pathTemplate, pathItem] of Object.entries<any>(spec.paths)) {
    for (const method of ["get", "post", "put", "patch", "delete"]) {
      const op = pathItem[method];
      if (!op || op.operationId !== operationId) continue;

      const fields: PlaygroundField[] = (op.parameters ?? []).map(
        (p: any) => ({
          name: p.name,
          in: p.in,
          type: p.schema?.type ?? "string",
          required: !!p.required,
          description: p.description,
        }),
      );

      // A JSON body can't be flattened into name/value rows the way a
      // form-urlencoded one can (`contacts` on POST /contacts is an array of
      // objects), so it is handed to the form as a single pre-filled
      // document instead.
      const jsonContentType = Object.keys(
        op.requestBody?.content ?? {},
      ).find((ct) => ct.includes("json"));
      const jsonBody = jsonContentType
        ? {
            contentType: jsonContentType,
            example: JSON.stringify(
              exampleFromSchema(
                spec,
                op.requestBody.content[jsonContentType]?.schema,
              ),
              null,
              2,
            ),
          }
        : undefined;

      const formSchema =
        op.requestBody?.content?.["application/x-www-form-urlencoded"]
          ?.schema;
      if (formSchema) {
        const required: string[] = formSchema.required ?? [];
        for (const [name, prop] of Object.entries<any>(
          formSchema.properties ?? {},
        )) {
          fields.push({
            name,
            in: "formData",
            type: prop.type ?? "string",
            required: required.includes(name),
            description: prop.description,
          });
        }
      }

      const responses: PlaygroundResponse[] = Object.entries<any>(
        op.responses ?? {},
      ).map(([status, res]) => {
        const contentTypes = Object.keys(res.content ?? {});
        const firstContent = res.content?.[contentTypes[0]];
        return {
          status,
          description: res.description ?? "",
          contentTypes,
          example: firstContent
            ? exampleFromSchema(spec, firstContent.schema)
            : null,
        };
      });

      const acceptContentTypes = [
        ...new Set(responses.flatMap((r) => r.contentTypes)),
      ];

      return {
        method: method.toUpperCase(),
        path: pathTemplate,
        server,
        summary: op.summary ?? operationId,
        description: op.description,
        fields,
        responses,
        acceptContentTypes,
        jsonBody,
      };
    }
  }

  throw new Error(`operationId "${operationId}" not found in spec`);
}
